"use client";

import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import Icon from "@/components/library/Icon";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import JotFormCallout from "./JotFormCallout";
import JotFormSection from "./JotFormSection";

interface JotFormPinnedFormsSectionProps {
  /** The saved list. Drives the unpinned warning; the editor works off a draft. */
  pins: string[];
  disabled: boolean;
  isSaving: boolean;
  onSave: (draft: string[]) => Promise<string[] | null>;
}

export default function JotFormPinnedFormsSection({
  pins,
  disabled,
  isSaving,
  onSave,
}: Readonly<JotFormPinnedFormsSectionProps>) {
  const { t } = useTranslation("partners");
  const { t: ct } = useTranslation("common");
  const [draft, setDraft] = useState<string[]>(pins);
  const [dirty, setDirty] = useState(false);
  const [entry, setEntry] = useState("");

  // A rotate re-reads the pins, as does a conflict. An untouched draft must
  // follow the server, or the next Save writes a stale list back over it.
  useEffect(() => {
    if (!dirty) setDraft(pins);
  }, [pins, dirty]);

  const handleAdd = useCallback(() => {
    const trimmed = entry.trim();
    if (!trimmed) return;
    setDraft((current) =>
      current.includes(trimmed) ? current : [...current, trimmed]
    );
    setDirty(true);
    setEntry("");
  }, [entry]);

  const handleRemove = useCallback((formId: string) => {
    setDraft((current) => current.filter((id) => id !== formId));
    setDirty(true);
  }, []);

  const handleSave = useCallback(async () => {
    // `onSave` rejects rather than returns when the action throws — an expired
    // session is rethrown out of the fetch layer on purpose. Folding that into
    // the null path keeps the rejection out of the click handler and leaves the
    // draft dirty, which is the same thing a conflict does.
    const saved = await onSave(draft).catch(() => null);
    // Adopt the saved list so the editor reflects server-side normalization.
    // On a conflict `onSave` returns null and the draft stays dirty, which is
    // what keeps what the user typed alive across the re-read.
    if (saved) {
      setDraft(saved);
      setDirty(false);
    }
  }, [draft, onSave]);

  return (
    <JotFormSection
      heading={t("jotformIntegration.pinsHeading")}
      testId="jotform-pinned-forms-section"
    >
      <p className="text-xs text-500">{t("jotformIntegration.pinsDescription")}</p>

      {pins.length === 0 && (
        <JotFormCallout
          tone="amber"
          message={t("jotformIntegration.pinsUnpinnedWarning")}
          testId="jotform-unpinned-warning"
        />
      )}

      {draft.length > 0 && (
        <ul className="space-y-1">
          {/* Keyed on the id itself, which `handleAdd` keeps unique. */}
          {draft.map((formId) => (
            <li
              key={formId}
              className="flex items-center gap-2 p-1.5 rounded bg-100 text-xs font-mono"
            >
              <span className="flex-1 break-all">{formId}</span>
              <button
                type="button"
                onClick={() => handleRemove(formId)}
                disabled={disabled}
                aria-label={t("jotformIntegration.removeFormId", { formId })}
                className="text-500 hover:text-900 disabled:opacity-50"
              >
                <Icon icon="xmark" />
              </button>
            </li>
          ))}
        </ul>
      )}

      <div className="flex gap-2">
        <Input
          type="text"
          value={entry}
          disabled={disabled}
          placeholder={t("jotformIntegration.addFormIdPlaceholder")}
          aria-label={t("jotformIntegration.addFormId")}
          onChange={(event) => setEntry(event.target.value)}
          onKeyDown={(event) => {
            if (event.key !== "Enter") return;
            event.preventDefault();
            handleAdd();
          }}
          className="flex-1 text-xs"
        />
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={handleAdd}
          disabled={disabled}
          className="shrink-0"
        >
          {ct("add")}
        </Button>
      </div>

      <div className="flex items-center gap-2">
        <Button
          type="button"
          size="sm"
          onClick={handleSave}
          disabled={disabled || isSaving}
        >
          {t("jotformIntegration.savePins")}
        </Button>
        {disabled && (
          <p className="text-xs text-500">{t("jotformIntegration.pinsRequireSecret")}</p>
        )}
      </div>
    </JotFormSection>
  );
}
